import { ENTITY_SPAWNING, EntityKind } from "../net/connection";

/** One effect event of a motion: which effect, how far into the motion,
 *  and the bone it attaches to, or null for the body's root. */
export interface MotionEffectEvent {
  readonly effect: string;
  readonly atS: number;
  readonly bone: string | null;
}

/** `metinstone_drop.msa`, the SPAWN motion `metinstone/motlist.txt:1` names,
 *  as its two effect events in `StartingTime` order. Both follow the stone:
 *  Event00 attaches to its bone (`ActorInstanceMotionEvent.cpp:127-134`),
 *  Event01 attaches nowhere and so rides the root
 *  (`ActorInstanceMotionEvent.cpp:162-165`). */
export const STONE_SPAWN_EVENTS: readonly MotionEffectEvent[] = [
  { effect: "metin_drop", atS: 0.079487, bone: "ChamferCyl04" }, // metinstone_drop.msa:12,14
  { effect: "metin_crash", atS: 0.448718, bone: null }, // metinstone_drop.msa:23,25
];

/** `metinstone_blow.msa`, the FRONT_DEAD and BACK_DEAD motion
 *  `metinstone/motlist.txt:6,12` names, as its two effect events in
 *  `StartingTime` order. Neither attaches, so both ride the root
 *  (`ActorInstanceMotionEvent.cpp:162-165`). */
export const STONE_DEAD_EVENTS: readonly MotionEffectEvent[] = [
  { effect: "metin_blow", atS: 0.280342, bone: null }, // metinstone_blow.msa:24,27
  { effect: "metin_laser", atS: 0.300855, bone: null }, // metinstone_blow.msa:12,15
];

export interface SpawnMotionField {
  has(key: string): boolean;
  spawn(key: string, options: { xMm: number; yMm: number; zMm: number; followId: number }): unknown;
}

export interface SpawningBody {
  readonly id: number;
  readonly kind: number;
  readonly flags: number;
  readonly xMm: number;
  readonly yMm: number;
}

/** Starts the SPAWN motion of every stone first seen carrying `ENTITY_SPAWNING`
 *  and plays its `STONE_SPAWN_EVENTS`: `CInstanceBase::Create` starts a non-PC
 *  in `NAME_SPAWN` when its add packet carries the spawn state
 *  (`InstanceBase.cpp:791-803`),
 *  and `MotionEventProcess` fires each event once when the motion passes its
 *  `StartingTime` (`ActorInstanceMotionEvent.cpp:12-35`). A stone that leaves
 *  takes the events it has not reached with it. */
export class StoneSpawnMotions {
  /** Entity id -> when its motion started, and how many events have fired. */
  private readonly running = new Map<number, { startMs: number; fired: number }>();

  frame<T extends SpawningBody>(
    field: SpawnMotionField,
    bodies: readonly T[],
    nowMs: number,
    anchor: (body: T, bone: string | null) => { xMm: number; yMm: number; zMm: number },
    start: (body: T, nowMs: number) => void = () => {},
  ): void {
    const present = new Set<number>();
    for (const body of bodies) {
      if (body.kind !== EntityKind.STONE) continue;
      present.add(body.id);
      let motion = this.running.get(body.id);
      if (motion === undefined) {
        if ((body.flags & ENTITY_SPAWNING) === 0) continue;
        motion = { startMs: nowMs, fired: 0 };
        this.running.set(body.id, motion);
        start(body, nowMs);
      }
      while (motion.fired < STONE_SPAWN_EVENTS.length) {
        const event = STONE_SPAWN_EVENTS[motion.fired];
        if (nowMs - motion.startMs < event.atS * 1000) break;
        motion.fired += 1;
        if (field.has(event.effect)) {
          field.spawn(event.effect, { ...anchor(body, event.bone), followId: body.id });
        }
      }
    }
    for (const id of [...this.running.keys()]) {
      if (!present.has(id)) this.running.delete(id);
    }
  }
}
